import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { useGetProductList } from "@/api/client/product";
import { getAllBrand, getBrandData } from "@/api/server/brand";
import { SortModal } from "@/components/actions";
import SortFilter from "@/components/actions/SortFilter";
import { SortIcon } from "@/components/data-display";
import ProductCard from "@/components/data-display/card/product";
import CategoryHeading from "@/components/data-display/categoryHeading";
import HtmlRenderer from "@/components/data-display/htmlRenderer";
import { HeroFeedback } from "@/components/feedback";
import CategoriesMenu from "@/components/navigation/categories";
import Pagination from "@/components/navigation/pagination";
import Testimonials from "@/components/sections/Testimonials";
import MetaData from "@/components/seo/MetaData";
import Breadcrumbs from "@/components/ui/navigation/breadcrumbs";
import useRouterPush from "@/hooks/useRouterPush";
import { getWishlistState } from "@/store/feature/wishlistSlice";
import statusCodes from "@/utils/constants/http-status-codes";
import { revalidationTime } from "@/utils/constants/server-side.constants";
import { getPageResult } from "@/utils/helpers/pageResult";
import { generateUrlToQuery } from "@/utils/helpers/queryString";
import { formatSortOptions } from "@/utils/helpers/sort";
import { updateListWithWishlistStatus } from "@/utils/helpers/wishlist";

function Brand({ brandData }) {
  const [brandMeta, setBrandMeta] = useState(brandData.meta);
  const [brandProducts, setBrandsProduct] = useState(brandData.products);
  const [isSortModal, setSortModal] = useState(false);

  const { wishlistItems } = useSelector(getWishlistState);
  const { queryParams, updateQueryParams } = useRouterPush();

  const { isLoading: isProductLoading } = useGetProductList(
    generateUrlToQuery("brands", queryParams),
    {
      enabled: Object.keys(queryParams).length > 1,
      onSuccess: (data) => {
        setBrandsProduct(
          updateListWithWishlistStatus(data.products, wishlistItems)
        );
        setBrandMeta(data.meta);
      },
    }
  );

  useEffect(() => {
    if (!Object.keys(wishlistItems).length) return;
    setBrandsProduct(
      updateListWithWishlistStatus(brandProducts, wishlistItems)
    );
  }, [Object.keys(wishlistItems).length]);

  return (
    <>
      <MetaData {...brandData?.metadata} schema={brandData?.schema} />

      <div className="container pb-[1rem]">
        <div className="my-[1.375rem]">
          <Breadcrumbs
            breadcrumbs={[
              { link: "", value: "Home" },
              { link: "", value: brandData.brand.name },
            ]}
          />
        </div>
        <section className="mt-[3.125rem] flex justify-between pb-5 lg:space-x-[4.375rem]">
          <div className="hidden w-[17%] lg:block">
            <CategoriesMenu categoryList={brandData?.categories} />
          </div>

          <div className="w-full lg:w-[80%]">
            <div className="mb-5 space-y-[1.25rem] md:mb-8">
              <CategoryHeading categoryName={brandData?.brand?.name} />
              <HtmlRenderer htmlData={brandData?.brand?.description} />
            </div>

            <div className="my-[2rem] flex items-center justify-between">
              <p>{getPageResult(brandMeta)}</p>
              <SortFilter sortOptions={brandData.sortables} />

              <div className="flex h-[2.5rem] w-[9.5rem] cursor-pointer items-center justify-center rounded-[2.625rem] bg-[#EBEEF2] md:hidden">
                <button
                  type="button"
                  onClick={() => setSortModal(true)}
                  className="flex w-full items-center justify-center gap-2 text-center text-[1.1rem] text-[#0A101A]"
                >
                  <SortIcon />
                  <span>Sort By</span>
                </button>
              </div>
            </div>

            {!isProductLoading && brandProducts.length === 0 && (
              <div className="w-full">
                <HeroFeedback
                  title="No product found!"
                  imageAltText="product-not-found"
                  imageURL="/assets/images/no-upcoming.png"
                />
              </div>
            )}

            {brandProducts.length > 0 && (
              <div className="grid grid-cols-2 gap-[0.75rem] lg:grid-cols-3 lg:gap-[1.563rem] xl:grid-cols-4">
                {brandProducts.map((product) => (
                  <ProductCard product={product} key={product.slug} />
                ))}
              </div>
            )}

            {brandMeta?.total > brandMeta?.per_page && (
              <div className="mt-[4]">
                <Pagination
                  initialPage={brandMeta.current_page - 1}
                  getPaginateData={(selectedPage) =>
                    updateQueryParams({ page: selectedPage + 1 })
                  }
                  totalPage={Math.ceil(brandMeta.total / brandMeta.per_page)}
                />
              </div>
            )}
          </div>
        </section>
      </div>

      {brandData?.testimonials?.length > 0 && (
        <Testimonials
          className="lg:!pt-[2.125rem]"
          data={brandData?.testimonials}
        />
      )}

      {!!brandData.brand?.long_description && (
        <div className="container my-[4.875rem]">
          <HtmlRenderer
            htmlData={brandData.brand?.long_description}
            visibleLine="line-clamp-none"
          />
        </div>
      )}

      <SortModal
        isOpen={isSortModal}
        onHide={() => setSortModal(false)}
        value={{ value: queryParams.sort_by }}
        sortData={formatSortOptions(brandData.sortables)}
        onChange={(e) => {
          updateQueryParams({ sort_by: e.target.value, page: 1 });
          setSortModal(false);
        }}
      />
    </>
  );
}

export const getStaticPaths = async () => {
  const { data } = await getAllBrand();
  const { brands } = data.data;

  const paths = brands.map((brand) => ({
    params: { slug: brand.slug },
  }));

  return {
    paths,
    fallback: "blocking",
  };
};

export const getStaticProps = async ({ params }) => {
  const props = { brandData: null };

  const brandRes = await getBrandData(params?.slug);
  if (brandRes?.status === statusCodes.OK) {
    props.brandData = brandRes.data?.data;
  }

  if (!props.brandData) {
    return { notFound: true };
  }

  return { props, revalidate: revalidationTime };
};

export default Brand;
